import React, { useState, useEffect, useCallback } from 'react';
import {
    initializeMusicKit,
    authorize as authorizeApple,
    isAuthorized as isAuthorizedApple,
    unauthorize as unauthorizeApple,
    addToLibraryBatched,
} from './services/musicKit.js';
import {
    initializeSpotify,
    authorize as authorizeSpotify,
    isAuthorized as isAuthorizedSpotify,
    unauthorize as unauthorizeSpotify,
    handleCallback as handleSpotifyCallback,
    saveTracksBatched,
} from './services/spotifyService.js';
import { parseInputText } from './utils/textParser.js';
import { matchAllSongs, getMatchSummary, getImportableResults } from './services/songMatcher.js';
import { matchAllSongsSpotify, getMatchSummarySpotify, getImportableResultsSpotify } from './services/spotifyMatcher.js';

const EXAMPLE_SONGS = `Bohemian Rhapsody - Queen
Hotel California - Eagles
Stairway to Heaven - Led Zeppelin
Billie Jean - Michael Jackson
Sweet Child O' Mine - Guns N' Roses`;

function App() {
    const [inputText, setInputText] = useState('');
    const [provider, setProvider] = useState('apple'); // 'apple' | 'spotify'
    const [isAuth, setIsAuth] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState(null);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);
    const [includeUncertain, setIncludeUncertain] = useState(true);

    // Handle Spotify OAuth callback
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const spotifyError = urlParams.get('error');

        if (spotifyError) {
            setError(`SPOTIFY_AUTH_ERROR: ${spotifyError}`);
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
        }

        if (code) {
            // Handle Spotify callback
            setProvider('spotify');
            handleSpotifyCallback(code)
                .then(() => {
                    setIsAuth(true);
                    setIsInitialized(true);
                    // Clean URL
                    window.history.replaceState({}, document.title, window.location.pathname);
                })
                .catch(err => {
                    setError(`SPOTIFY_CALLBACK_ERROR: ${err.message}`);
                    setIsInitialized(true);
                    window.history.replaceState({}, document.title, window.location.pathname);
                });
            return;
        }

        // Normal initialization
        initServices();
    }, []);

    async function initServices() {
        try {
            // Initialize both services
            const [,] = await Promise.all([
                initializeMusicKit().catch(err => {
                    console.warn('[Init] MusicKit init failed:', err.message);
                }),
                initializeSpotify().catch(err => {
                    console.warn('[Init] Spotify init failed:', err.message);
                }),
            ]);

            setIsInitialized(true);

            // Check auth status for current provider
            await checkAuthStatus();
        } catch (err) {
            setError(`INTERNAL_ERROR: ${err.message}`);
            setIsInitialized(true);
        }
    }

    async function checkAuthStatus() {
        if (provider === 'apple') {
            const authorized = await isAuthorizedApple();
            setIsAuth(authorized);
        } else {
            const authorized = isAuthorizedSpotify();
            setIsAuth(authorized);
        }
    }

    // Re-check auth when provider changes
    useEffect(() => {
        if (isInitialized) {
            checkAuthStatus();
            setResults(null); // Clear results when switching providers
        }
    }, [provider, isInitialized]);

    const handleAuthorize = useCallback(async () => {
        try {
            setError(null);
            if (provider === 'apple') {
                await authorizeApple();
                setIsAuth(true);
            } else {
                // Spotify redirects to their auth page
                await authorizeSpotify();
                // After redirect, the useEffect will handle the callback
            }
        } catch (err) {
            setError(`AUTH_FAILED: ${err.message}`);
        }
    }, [provider]);

    const handleSignOut = useCallback(async () => {
        try {
            if (provider === 'apple') {
                await unauthorizeApple();
            } else {
                await unauthorizeSpotify();
            }
            setIsAuth(false);
            setResults(null);
        } catch (err) {
            setError(`SIGNOUT_FAILED: ${err.message}`);
        }
    }, [provider]);

    const handleImport = useCallback(async () => {
        if (!inputText.trim()) {
            setError('INPUT_REQUIRED: PASTE TRACKLIST');
            return;
        }
        setIsLoading(true);
        setError(null);
        setResults(null);

        try {
            setProgress({ phase: '01', message: 'PARSING_DATA', completed: 0, total: 100 });
            const parsed = parseInputText(inputText);
            if (parsed.length === 0) throw new Error('DATA_FORMAT_ERROR');

            setProgress({ phase: '02', message: 'MATCHING_CATALOG', completed: 0, total: parsed.length });

            let matches, summary, toImport;

            if (provider === 'apple') {
                matches = await matchAllSongs(parsed, {
                    onProgress: (p) => setProgress({ phase: '02', message: `FINDING_SONGS_${p.completed}/${p.total}`, ...p })
                });
                toImport = getImportableResults(matches, { includeUncertain });
                summary = getMatchSummary(matches);
            } else {
                matches = await matchAllSongsSpotify(parsed, {
                    onProgress: (p) => setProgress({ phase: '02', message: `FINDING_SONGS_${p.completed}/${p.total}`, ...p })
                });
                toImport = getImportableResultsSpotify(matches, { includeUncertain });
                summary = getMatchSummarySpotify(matches);
            }

            if (toImport.length === 0) {
                setResults({ matches, summary, imported: 0, importFailed: 0 });
                setProgress(null);
                setIsLoading(false);
                return;
            }

            setProgress({ phase: '03', message: 'INJECTING_LIBRARY', completed: 0, total: toImport.length });
            const songIds = toImport.map(m => m.match.id);

            let importResult;
            if (provider === 'apple') {
                importResult = await addToLibraryBatched(songIds, {
                    onProgress: (p) => setProgress({ phase: '03', message: `PUSHING_${p.processed}/${p.total}`, completed: p.processed, total: p.total })
                });
            } else {
                importResult = await saveTracksBatched(songIds, {
                    onProgress: (p) => setProgress({ phase: '03', message: `PUSHING_${p.processed}/${p.total}`, completed: p.processed, total: p.total })
                });
            }

            setResults({ matches, summary, imported: importResult.success, importFailed: importResult.failed });
        } catch (err) {
            setError(`EXECUTION_FAILED: ${err.message}`);
        } finally {
            setIsLoading(false);
            setProgress(null);
        }
    }, [inputText, includeUncertain, provider]);

    const handleReset = useCallback(() => {
        setResults(null);
        setInputText('');
        setError(null);
    }, []);

    const handleKeyDown = (e) => {
        if (e.key === 'Tab' && !inputText.trim()) {
            e.preventDefault();
            setInputText(EXAMPLE_SONGS);
        }
    };

    const parsedCount = parseInputText(inputText).length;
    const providerLabel = provider === 'apple' ? 'APPLE MUSIC' : 'SPOTIFY';

    if (!isInitialized) return null; // Let the index.html loader show

    return (
        <div className="brutalist-root">
            <div className="sidebar-text">MUSIC IMPORTER V.2.0</div>

            <div className={`marquee-container ${isAuth && provider === 'apple' ? 'connected-apple' : ''}`}>
                <div className="marquee-content">
                    <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                    <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                    <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                    <span>{isAuth ? `${providerLabel} / CONNECTED` : `${providerLabel} / WAITING`}</span>
                </div>
            </div>

            <div className="brutalist-grid">
                <header className="header-block">
                    <div className="header-meta">
                        <span style={{ fontWeight: 800 }}>IMPORTER.V2</span>
                        <span style={{ fontFamily: 'var(--font-mono)' }}>{new Date().toISOString()}</span>
                    </div>
                    <h1 className="huge-title">
                        Push <br /> Your <br /> Music
                    </h1>
                </header>

                {error && (
                    <div className="card" style={{ gridColumn: 'span 12', borderColor: 'var(--error)', marginBottom: 'var(--s4)' }}>
                        <span className="brutalist-label" style={{ color: 'var(--error)' }}>! ERROR_DETECTED</span>
                        <p style={{ fontFamily: 'var(--font-mono)' }}>{error}</p>
                        <button onClick={() => setError(null)} className="brutalist-btn ghost" style={{ marginTop: 'var(--s2)' }}>DISMISS</button>
                    </div>
                )}

                <div className="auth-block">
                    <div className="card">
                        <h2 className="brutalist-label">01 / Provider & Connection</h2>

                        <div className="provider-selector">
                            <button
                                onClick={() => setProvider('apple')}
                                className={`provider-btn apple ${provider === 'apple' ? 'active' : ''} ${isAuth && provider === 'apple' ? 'connected' : ''}`}
                                disabled={isLoading}
                            >
                                APPLE MUSIC
                            </button>
                            <div className="provider-btn-wrapper">
                                <button
                                    className="provider-btn spotify disabled"
                                    disabled={true}
                                    title="Spotify temporarily unavailable for new apps"
                                >
                                    SPOTIFY
                                </button>
                                <span className="coming-soon-badge">SOON</span>
                                <div className="provider-tooltip">
                                    Spotify has paused new developer registrations. This will be enabled as soon as they reopen.
                                </div>
                            </div>
                        </div>

                        <div className="auth-content">
                            {!isAuth ? (
                                <>
                                    <p>AUTHORIZE ACCESS TO {providerLabel} LIBRARY.</p>
                                    <button onClick={handleAuthorize} className="brutalist-btn">CONNECT.V1</button>
                                </>
                            ) : (
                                <div className="btn-group">
                                    <button className="brutalist-btn accent">ACTIVE</button>
                                    <button onClick={handleSignOut} className="brutalist-btn ghost">EXIT</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {!results ? (
                    <div className="input-block">
                        <div className="card">
                            <h2 className="brutalist-label">02 / Tracklist</h2>
                            <textarea
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={EXAMPLE_SONGS}
                                className="brutalist-textarea"
                                disabled={isLoading}
                            />
                            <div className="options-row">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={includeUncertain}
                                        onChange={(e) => setIncludeUncertain(e.target.checked)}
                                        className="checkbox-input"
                                    />
                                    UNCERTAIN_MATCHES
                                </label>
                                <span className="song-count">
                                    DATA_NODES: <span className="song-count-number">{parsedCount}</span>
                                </span>
                            </div>
                            <p className="input-helper-text">
                                Allows approximate matches (e.g. remixes). Uncheck for strict accuracy.
                            </p>

                            {progress && (
                                <div className="brutalist-progress">
                                    <div className="progress-fill" style={{ width: `${(progress.completed / progress.total) * 100}%` }} />
                                    <span className="progress-label">{progress.message}</span>
                                </div>
                            )}

                            <button
                                onClick={handleImport}
                                disabled={!isAuth || isLoading || !inputText.trim()}
                                className="brutalist-btn btn-full"
                                style={{ marginTop: 'var(--s4)' }}
                            >
                                {isLoading ? 'EXECUTING...' : `SYNC_TO_${provider === 'apple' ? 'ICLOUD' : 'SPOTIFY'}`}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="results-block">
                        <div className="card">
                            <h2 className="brutalist-label">03 / Output_Log ({providerLabel})</h2>

                            <div className="stats-row">
                                <div className="stat-item">
                                    <div className="stat-value">{results.imported}</div>
                                    <div className="stat-key">SUCCESS</div>
                                </div>
                                <div className="stat-item">
                                    <div className="stat-value">{results.summary.matched}</div>
                                    <div className="stat-key">MATCHED</div>
                                </div>
                                <div className="stat-item">
                                    <div className="stat-value" style={{ color: results.summary.notFound > 0 ? 'red' : 'inherit' }}>{results.summary.notFound}</div>
                                    <div className="stat-key">LOST</div>
                                </div>
                            </div>

                            <div className="track-list">
                                {results.matches.map((item, i) => (
                                    <div key={i} className={`track-row ${item.status === 'not_found' ? 'status--error' : ''}`}>
                                        <div className={`track-status ${item.status === 'matched' ? 'status--success' : ''}`}>
                                            {item.status === 'matched' ? '✓' : item.status === 'uncertain' ? '?' : '✗'}
                                        </div>
                                        <div className="track-info">
                                            <div className="track-original">{item.originalLine}</div>
                                            {item.match && <div className="track-match">{item.match.title} // {item.match.artist}</div>}
                                        </div>
                                        <div className="match-score">
                                            {item.match ? `${Math.round(item.matchScore * 100)}%` : '0%'}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button onClick={handleReset} className="brutalist-btn ghost btn-full" style={{ marginTop: 'var(--s4)' }}>RESTART_V1</button>
                        </div>
                    </div>
                )}
            </div>

            <footer style={{ marginTop: 'var(--s8)', borderTop: 'var(--border)', paddingTop: 'var(--s2)', fontSize: '0.75rem', fontWeight: 700 }}>
                [SYSTEM: OK] // [LIB: {provider === 'apple' ? 'MUSICKIT_V3' : 'SPOTIFY_WEB_API'}] // [CORE: REACT_18]
            </footer>
        </div>
    );
}

export default App;
